import {Component, Emitter} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {TouchUI} from '../ui/touch-ui.js';
import {isMobile} from '../utils/mobile.js';

/**
 * control-events
 */

// Define the interface for key states
export interface KeyState {
    [key: string]: boolean;
    up: boolean;
    down: boolean;
    left: boolean;
    right: boolean;
    dash: boolean;
    jump: boolean;
}

export class ControlEvents extends Component {
    static TypeName = 'control-events';

    private previousPatternName = '';
    private patterns: {pattern: number[]; name: string}[] = [];

    static onForwardStart: Emitter<[KeyboardEvent]> = new Emitter();
    static onForwardEnd: Emitter<[KeyboardEvent]> = new Emitter();
    static onLeftStart: Emitter<[KeyboardEvent]> = new Emitter();
    static onLeftEnd: Emitter<[KeyboardEvent]> = new Emitter();
    static onRightStart: Emitter<[KeyboardEvent]> = new Emitter();
    static onRightEnd: Emitter<[KeyboardEvent]> = new Emitter();
    static onBackwardStart: Emitter<[KeyboardEvent]> = new Emitter();
    static onBackwardEnd: Emitter<[KeyboardEvent]> = new Emitter();
    static onKeyDown: Emitter<[KeyboardEvent, KeyState]> = new Emitter();
    static onKeyUp: Emitter<[KeyboardEvent, KeyState]> = new Emitter();
    static onClick: Emitter<[MouseEvent]> = new Emitter();
    static onMouseMove: Emitter<[MouseEvent]> = new Emitter();
    static onMouseDown: Emitter<[MouseEvent]> = new Emitter();
    static onPointerDown: Emitter<[PointerEvent]> = new Emitter();
    static onPointerUp: Emitter<[PointerEvent]> = new Emitter();
    static onPointerMove: Emitter<[PointerEvent]> = new Emitter();
    static onTouchMove: Emitter<[TouchEvent]> = new Emitter();

    static onContextMenu: Emitter<[MouseEvent]> = new Emitter();
    static onStateChange: Emitter<[string]> = new Emitter();
    static onDashStart: Emitter<[]> = new Emitter();
    static onJumpStart: Emitter<[]> = new Emitter();
    static onPowerStart: Emitter<[]> = new Emitter();
    static onDashEnd: Emitter<[]> = new Emitter();
    static onJumpEnd: Emitter<[]> = new Emitter();
    static onPowerEnd: Emitter<[]> = new Emitter();
    static onAttackStart: Emitter<[]> = new Emitter();

    private jumpKeyDownTriggered = false;
    private dashKeyDownTriggered = false;
    private powerKeyDownTriggered = false;

    keyState: KeyState = {
        up: false,
        down: false,
        left: false,
        right: false,
        dash: false,
        jump: false,
        power: false,
    };

    forwardCodes = ['KeyW', 'ArrowUp', 'Numpad8'];
    backwardCodes = ['KeyS', 'ArrowDown', 'Numpad2'];
    leftCodes = ['KeyA', 'ArrowLeft', 'Numpad4'];
    rightCodes = ['KeyD', 'ArrowRight', 'Numpad6'];
    dashCodes = ['KeyQ', 'ShiftLeft'];
    jumpCodes = ['Space'];
    powerCodes = ['KeyE'];

    start() {
        this.previousPatternName = '';
        this.initPatterns();

        window.addEventListener('keydown', this.keyDown.bind(this));
        window.addEventListener('keyup', this.keyUp.bind(this));

        const canvas = this.engine.canvas;
        window.addEventListener('click', this.click.bind(this));
        window.addEventListener('mousedown', this.mouseDown.bind(this));
        window.addEventListener('mousemove', this.mouseMove.bind(this));
        this.engine.canvas.addEventListener('touchmove', this.touchMove.bind(this));
        window.addEventListener('pointermove', this.pointerMove.bind(this));
        canvas.addEventListener('contextmenu', this.contextMenu.bind(this));
    }

    private initPatterns() {
        this.patterns = [
            {pattern: [1, 0, 0, 0], name: 'up'},
            {pattern: [0, 0, 1, 0], name: 'left'},
            {pattern: [0, 0, 0, 1], name: 'right'},
            {pattern: [1, 0, 1, 0], name: 'upRight'},
            {pattern: [1, 0, 0, 1], name: 'upLeft'},
            {pattern: [0, 1, 0, 0], name: 'down'},
            {pattern: [0, 1, 1, 0], name: 'downRight'},
            {pattern: [0, 1, 0, 1], name: 'downLeft'},
            {pattern: [0, 0, 0, 0], name: 'notMoving'},
        ];
    }

    update(dt: number) {
        if (isMobile) {
            this.TouchUpdate();
        }
        /* Called every frame. */
        let keyArray = [0, 0, 0, 0];

        for (const key in this.keyState) {
            if (Object.hasOwnProperty.call(this.keyState, key)) {
                const value = this.keyState[key];
                // Map values to key array
                switch (key) {
                    case 'up':
                        keyArray[0] = value ? 1 : 0;
                        break;
                    case 'down':
                        keyArray[1] = value ? 1 : 0;
                        break;
                    case 'left':
                        keyArray[2] = value ? 1 : 0;
                        break;
                    case 'right':
                        keyArray[3] = value ? 1 : 0;
                        break;
                    default:
                        break;
                }
            }
        }

        // Find the matched pattern
        let matchedPattern = this.patterns.find(({pattern}) =>
            this.checkKeyArray(keyArray, pattern)
        );

        if (matchedPattern) {
            if (this.previousPatternName != matchedPattern.name) {
                ControlEvents.onStateChange.notify(matchedPattern.name.toString());
            }
            this.previousPatternName = matchedPattern.name;
        }
    }

    TouchUpdate() {
        const touchUI = this.object?.getComponent(TouchUI)!;
        if (!touchUI) return; // Ensure TouchUI component exists

        const leftJoystickX = touchUI?.joystick1Value?.x;
        const leftJoystickY = touchUI?.joystick1Value?.y;

        // Reset key states
        this.keyState.up = false;
        this.keyState.down = false;
        this.keyState.left = false;
        this.keyState.right = false;

        // Determine movement direction based on joystick position
        if (leftJoystickY > 0.5) {
            this.keyState.down = true;
        } else if (leftJoystickY < -0.5) {
            this.keyState.up = true;
        }

        if (leftJoystickX < -0.5) {
            this.keyState.left = true;
        } else if (leftJoystickX > 0.5) {
            this.keyState.right = true;
        }
    }

    checkKeyArray(keyArray: number[], pattern: number[]): boolean {
        if (keyArray.length !== pattern.length) {
            return false; // Arrays must have the same length
        }

        for (let i = 0; i < keyArray.length; i++) {
            if (keyArray[i] !== pattern[i]) {
                return false; // Array elements don't match the pattern
            }
        }

        return true; // Array matches the pattern
    }

    private click(event: MouseEvent) {
        ControlEvents.onClick.notify(event);
    }
    private mouseDown(event: MouseEvent) {
        ControlEvents.onMouseDown.notify(event);
        if (event.button === 0) {
            ControlEvents.onAttackStart.notify();
        }
    }

    private mouseMove(event: MouseEvent) {
        ControlEvents.onMouseMove.notify(event);
    }
    private touchMove(event: TouchEvent) {
        ControlEvents.onTouchMove.notify(event);
    }

    /* pointerDown handled in hud.tsx */
    /* pointerUp handled in hud.tsx */

    private pointerMove(event: PointerEvent) {
        ControlEvents.onPointerMove.notify(event);
    }
    private contextMenu(event: MouseEvent) {
        ControlEvents.onContextMenu.notify(event);
    }

    private keyDown(event: KeyboardEvent) {
        if (this.forwardCodes.includes(event.code)) {
            this.keyState.up = true;
            ControlEvents.onForwardStart.notify(event);
        }
        if (this.backwardCodes.includes(event.code)) {
            this.keyState.down = true;
            ControlEvents.onBackwardStart.notify(event);
        }
        if (this.leftCodes.includes(event.code)) {
            this.keyState.left = true;
            ControlEvents.onLeftStart.notify(event);
        }
        if (this.rightCodes.includes(event.code)) {
            this.keyState.right = true;
            ControlEvents.onRightStart.notify(event);
        }

        if (this.dashCodes.includes(event.code) && !this.dashKeyDownTriggered) {
            this.keyState.dash = true;
            ControlEvents.onDashStart.notify();
            this.dashKeyDownTriggered = true;
        }
        if (this.jumpCodes.includes(event.code) && !this.jumpKeyDownTriggered) {
            // Trigger onJumpStart only if it hasn't been triggered before
            ControlEvents.onJumpStart.notify();
            // Set the flag to true to indicate that onJumpStart has been triggered
            this.jumpKeyDownTriggered = true;
        }

        if (this.powerCodes.includes(event.code) && !this.powerKeyDownTriggered) {
            this.keyState.power = true;
            ControlEvents.onPowerStart.notify();
            this.powerKeyDownTriggered = true;
        }

        ControlEvents.onKeyDown.notify(event, this.keyState);
    }

    private keyUp(event: KeyboardEvent) {
        ControlEvents.onKeyUp.notify(event, this.keyState);
        if (this.forwardCodes.includes(event.code)) {
            this.keyState.up = false;
            ControlEvents.onForwardEnd.notify(event);
        }
        if (this.backwardCodes.includes(event.code)) {
            this.keyState.down = false;
            ControlEvents.onBackwardEnd.notify(event);
        }
        if (this.leftCodes.includes(event.code)) {
            this.keyState.left = false;
            ControlEvents.onLeftEnd.notify(event);
        }
        if (this.rightCodes.includes(event.code)) {
            this.keyState.right = false;
            ControlEvents.onRightEnd.notify(event);
        }
        if (this.dashCodes.includes(event.code)) {
            ControlEvents.onDashEnd.notify();
            this.keyState.dash = false;
            this.dashKeyDownTriggered = false;
        }
        if (this.jumpCodes.includes(event.code)) {
            ControlEvents.onJumpEnd.notify();
            this.keyState.jump = false;
            this.jumpKeyDownTriggered = false;
        }
        if (this.powerCodes.includes(event.code)) {
            ControlEvents.onPowerEnd.notify();
            this.keyState.power = false;
            this.powerKeyDownTriggered = false;
        }
    }
}
